import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useEscapeLayer } from './Dialog';
import { placeMenu } from './menuPlacement';

/**
 * `checked`: a toggle (role menuitemcheckbox) drawn with a tick when on, e.g. "Przenoś zmiany ze sprzętówki" (owner
 * 20.10 23:37: rarer switches under "…" too). `disabled`: shown but not choosable (a right the role lacks). `title`: a
 * tooltip of the item (e.g. that the choice replaces a typed price).
 */
export type RowAction = { label: string; onSelect: () => void; danger?: boolean; checked?: boolean; disabled?: boolean; title?: string | undefined };
const ITEMS = '[role="menuitem"],[role="menuitemcheckbox"]';
/** The place itself is set by the layout effect (measured), so a re-render does not move the list back. */
const FIXED = { position: 'fixed' } as const;

/**
 * "…" button with the rarer actions of a row (owner's rule: show them when needed).
 * Keyboard as in a menu: ↑ / ↓ move (wrapping), Home / End jump to the ends. Closes on a choice, a click
 * outside, Tab or focus leaving it, or Escape (focus goes back to the button).
 */
/** `icon="gear"`: the small settings gear at the right of a header (rule 9 of docs/wyglad.md) instead of "…". */
/** `icon="chevron"`: a small down arrow beside a value (the event's status: "Oznacz jako zakończone"); the list opens to the right. */
/** `text`: a small text button (e.g. "+ Usługa", P8) instead of the icon; `label` stays its accessible name. */
/**
 * The list is always placed `fixed` against the window (`placeMenu`, owner 2026-11-01: the invoices' „⋯” ran off the
 * window): a box that scrolls or clips (a wide table, a dialog) cannot cut it, it flips / opens upwards to stay in the
 * window, 8 px from its edges, and follows scrolling and resizing. `floating` only says the side it prefers: `true` its
 * left edge under the button's, `"end"` (the default for "…" and the gear) its right edge under the button's.
 */
export function RowMenu({ label, actions, icon = 'more', featureId, text, floating = false }: { label: string; actions: RowAction[]; icon?: 'more' | 'gear' | 'chevron'; featureId?: string; text?: string; floating?: boolean | 'end' }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const prefer = floating === true || text || icon === 'chevron' ? 'start' : 'end';

  useLayoutEffect(() => {
    if (!open) return;
    const place = (e?: Event) => {
      const el = pop.current;
      const btn = button.current;
      if (!el || !btn) return;
      // scrolling inside the list itself (a long one) moves nothing
      if (e?.target instanceof Node && el.contains(e.target)) return;
      const r = btn.getBoundingClientRect();
      // what is really visible: the visual viewport when there is one (a pinch zoom, a phone's keyboard)
      const vv = window.visualViewport;
      const view = vv ? { left: vv.offsetLeft, top: vv.offsetTop, width: vv.width, height: vv.height } : { left: 0, top: 0, width: window.innerWidth, height: window.innerHeight };
      // the button scrolled out of view: its list goes too (it would hang over something else)
      if (r.bottom < view.top || r.top > view.top + view.height) {
        el.style.visibility = 'hidden';
        return;
      }
      el.style.visibility = '';
      // its natural size first (with the CSS max-height of a long list), then the place
      Object.assign(el.style, { left: '0px', top: '0px', right: 'auto', bottom: 'auto', maxHeight: '', overflowY: '' });
      const p = placeMenu(r, { width: el.offsetWidth, height: el.offsetHeight }, view, prefer);
      el.style.left = `${p.left}px`;
      el.style.top = `${p.top}px`;
      if (p.maxHeight !== undefined) Object.assign(el.style, { maxHeight: `${p.maxHeight}px`, overflowY: 'auto' });
      // an ancestor with a transform / filter makes `fixed` relative to itself: correct by where the list really is
      const got = el.getBoundingClientRect();
      const dx = got.left - p.left;
      const dy = got.top - p.top;
      if (Math.abs(dx) > 0.5 || Math.abs(dy) > 0.5) {
        el.style.left = `${p.left - dx}px`;
        el.style.top = `${p.top - dy}px`;
      }
    };
    place();
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    window.visualViewport?.addEventListener('resize', place);
    window.visualViewport?.addEventListener('scroll', place);
    return () => {
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('resize', place);
      window.visualViewport?.removeEventListener('scroll', place);
    };
  }, [open, prefer]);

  useEffect(() => {
    if (!open) return;
    root.current?.querySelector<HTMLElement>(ITEMS)?.focus();
    const onDown = (e: PointerEvent) => {
      if (e.target instanceof Node && !root.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [open]);
  // Escape closes only the list (a dialog it sits in stays open), focus goes back to the button
  useEscapeLayer(() => {
    setOpen(false);
    button.current?.focus();
  }, open);

  const onMenuKey = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const items = [...e.currentTarget.querySelectorAll<HTMLElement>(ITEMS)];
    const at = items.indexOf(document.activeElement as HTMLElement);
    const go = (i: number) => {
      e.preventDefault();
      items[(i + items.length) % items.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(items.length - 1);
    else if (e.key === 'Tab') setOpen(false); // focus moves on as usual
  };
  // Close only when focus really moves somewhere outside. A blur with no new target is Safari pressing another item
  // (Safari does not focus buttons on click): closing then would swallow the click. So is focus going to an element
  // AROUND the menu (a dialog box, the page's main area with a `tabindex`): Safari focuses that one instead of the
  // pressed item (owner 2026-10-23 16:14: the second "Usuń wydarzenie" did nothing). Clicks outside close via pointerdown.
  const onBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const to = e.relatedTarget;
    if (to instanceof Node && !root.current?.contains(to) && !to.contains(root.current)) setOpen(false);
  };

  if (actions.length === 0) return null;
  return (
    <div className={text ? 'row-menu text' : icon === 'chevron' ? 'row-menu chevron' : 'row-menu'} ref={root} data-feature-id={featureId}>
      <button ref={button} type="button" className={text ? 'btn small ghost' : 'btn icon'} data-icon={text ? undefined : icon} title={icon === 'gear' ? label : undefined} aria-label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen((v) => !v)}>
        {text ? (
          text
        ) : icon === 'gear' ? (
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        ) : icon === 'chevron' ? (
          <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3.5 8h.01M8 8h.01M12.5 8h.01" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
          </svg>
        )}
      </button>
      {open && (
        <div className="row-menu-pop" role="menu" id={id} aria-label={label} onKeyDown={onMenuKey} onBlur={onBlur} ref={pop} style={FIXED}>
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              role={a.checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
              aria-checked={a.checked}
              disabled={a.disabled}
              title={a.title}
              className={[a.danger ? 'danger' : '', a.checked !== undefined ? 'toggle' : ''].filter(Boolean).join(' ') || undefined}
              onClick={() => {
                setOpen(false);
                // the item leaves the page: the button keeps the focus, so a dialog opened by it gives the focus back there
                button.current?.focus();
                a.onSelect();
              }}
            >
              {a.label}
              {/* a small box at the right end, ticked when on: the label stays in line with the other items */}
              {a.checked !== undefined && (
                <svg className="row-menu-tick" width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
                  <rect x="1.5" y="1.5" width="13" height="13" rx="3.5" fill={a.checked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.2" />
                  {a.checked && <path d="M4.6 8.3l2.3 2.3 4.5-5" fill="none" stroke="var(--surface)" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />}
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
